import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, resolveComponent as _resolveComponent } from "vue"

import { ref, computed } from "vue";
import {
  StepperStep,
  IdentifierSchema,
  VersionSchema,
  MinimalProps,
} from "@/types";
import { versionToString } from "@/util";
import { getVersionApiStore, permissionStore, projectStore } from "@/hooks";
import { Stepper, PanelCard } from "@/components/common";
import FlexBox from "@/components/common/display/content/FlexBox.vue";
import { ProjectSelectorTable, VersionSelectorTable } from "./table";


/**
 * Presents a stepper in a modal for selecting a project and version.
 */
const __default__ = {
  name: "ProjectVersionStepper",
};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  props: {
    minimal: { type: Boolean },
    teamOnly: { type: Boolean }
  },
  setup(__props: any) {

const props = __props;

const defaultProjectStep = (): StepperStep => ({
  title: "Select a Project",
  done: false,
});
const defaultVersionStep = (): StepperStep => ({
  title: "Select a Version",
  done: false,
});

const currentStep = ref(1);
const steps = ref([defaultProjectStep(), defaultVersionStep()]);
const selectedProject = ref<IdentifierSchema>();
const selectedVersion = ref<VersionSchema>();

const isProjectStep = computed(() => currentStep.value === 1);
const isVersionStep = computed(() => currentStep.value === 2);
const panelColor = computed(() => (props.minimal ? "transparent" : undefined));

/**
 * Clears all modal data.
 */
function handleClear() {
  const isProjectDefined = projectStore.isProjectDefined;

  selectedProject.value = isProjectDefined ? projectStore.project : undefined;
  selectedVersion.value = undefined;
  currentStep.value = 1;

  steps.value = [
    isProjectDefined
      ? {
          title: projectStore.project.name,
          done: true,
        }
      : defaultProjectStep(),
    defaultVersionStep(),
  ];
}

/**
 * Selects a project.
 * @param project - The project to select, or an empty selection.
 */
function handleProjectSelect(project: IdentifierSchema | undefined) {
  selectedProject.value = project;

  if (project) {
    currentStep.value++;
    steps.value[0] = {
      title: project.name,
      done: true,
    };
  } else {
    steps.value = [defaultProjectStep(), defaultVersionStep()];
  }

  handleVersionSelect(undefined);
}

/**
 * Selects a version.
 * @param version - The version to select, or an empty selection.
 */
function handleVersionSelect(version: VersionSchema | undefined) {
  selectedVersion.value = version;

  if (version) {
    steps.value[1] = {
      title: versionToString(version),
      done: true,
    };

    handleSubmit();
  } else {
    steps.value[1] = defaultVersionStep();
  }
}

/**
 * Loads the selected project.
 */
function handleSubmit(): void {
  if (
    !selectedProject.value ||
    !selectedVersion.value ||
    getVersionApiStore.loadLoading
  )
    return;

  getVersionApiStore
    .handleLoad(selectedVersion.value.versionId)
    .then(() => handleClear());
}

return (_ctx: any,_cache: any) => {
  const _component_q_circular_progress = _resolveComponent("q-circular-progress")!

  return (_openBlock(), _createBlock(_unref(PanelCard), { color: panelColor.value }, {
    default: _withCtx(() => [
      (!_unref(permissionStore).isDemo || !_unref(getVersionApiStore).loadLoading)
        ? (_openBlock(), _createBlock(_unref(Stepper), {
            key: 0,
            modelValue: currentStep.value,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((currentStep).value = $event)),
            steps: steps.value,
            minimal: props.minimal,
            loading: _unref(getVersionApiStore).loadLoading,
            "data-cy": "project-version-stepper",
            onSubmit: handleSubmit
          }, {
            "1": _withCtx(() => [
              _createVNode(_unref(ProjectSelectorTable), {
                minimal: props.minimal,
                open: isProjectStep.value,
                "team-only": props.teamOnly,
                onSelected: handleProjectSelect
              }, null, 8, ["minimal", "open", "team-only"])
            ]),
            "2": _withCtx(() => [
              (selectedProject.value)
                ? (_openBlock(), _createBlock(_unref(VersionSelectorTable), {
                    key: 0,
                    minimal: props.minimal,
                    disabled: _unref(getVersionApiStore).loadLoading,
                    open: isVersionStep.value,
                    project: selectedProject.value,
                    onSelected: handleVersionSelect
                  }, null, 8, ["minimal", "disabled", "open", "project"]))
                : _createCommentVNode("", true)
            ]),
            _: 1
          }, 8, ["modelValue", "steps", "minimal", "loading"]))
        : (_openBlock(), _createBlock(FlexBox, {
            key: 1,
            align: "center",
            justify: "center",
            "full-width": "",
            t: "2"
          }, {
            default: _withCtx(() => [
              _createVNode(_component_q_circular_progress, {
                rounded: "",
                indeterminate: "",
                size: "60px",
                thickness: 0.2,
                color: "primary"
              })
            ]),
            _: 1
          }))
    ]),
    _: 1
  }, 8, ["color"]))
}
}

})